import { ReactNode, useState } from "react";
import {
	Box,
	ClickAwayListener,
	Divider,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemIcon,
	MenuItem,
	MenuList,
	Popover,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { v4 as uuid } from "uuid";

/**
 * AppMenuSelectItemOption represents options passed to SelectItem UI component.
 */
export type AppMenuSelectItemOption<T> = {
	value: T;
	text: string;
	showDivider?: boolean;
	icon?: ReactNode;
};

interface Props<T> {
	value: T;
	title: string;
	icon: ReactNode;
	menuIsOpen: boolean;
	onChange: (newValue: T) => void;
	options: AppMenuSelectItemOption<T>[];
}

/**
 * SelectItem renders a special single item of the application side menu which
 * allows users to select an option out of a list of options.
 *
 * When the menu is closed, the item renders an icon button that presents a popover
 * for selecting an option out of a known list of items.
 *
 * When the menu is opened, the item renders a full button that presents a drop down
 * list which works the same way as above.
 *
 * @param value      the selected value
 * @param title      the item title
 * @param icon       the icon rendered when menu is closed
 * @param options    the popup menu available options
 * @param onChange   the handler that is invoked when the user select an option from the popup menu
 * @param menuIsOpen this flag is used to correctly render the menu item
 * @returns          an item of the application side menu
 */
const SelectItem = <T,>({ value, title, icon, options, onChange, menuIsOpen }: Props<T>) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	const open = Boolean(anchor);
	const id = open ? uuid() : undefined;

	const showMenu = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
		setAnchor(e.currentTarget);
	};

	const closeMenu = () => {
		setAnchor(null);
	};

	const didSelectOption = (option: AppMenuSelectItemOption<T>) => {
		onChange(option.value);
		closeMenu();
	};

	return (
		<ListItem disablePadding sx={{ display: "block" }}>
			{menuIsOpen && (
				<ListItemButton onClick={(e) => showMenu(e)} sx={{ minHeight: 48 }}>
					<Stack direction={"row"} alignItems="center">
						<ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>{icon}</ListItemIcon>
						<Typography>{title}</Typography>
						<Box flexGrow={1} />
					</Stack>
				</ListItemButton>
			)}

			{!menuIsOpen && (
				<Tooltip arrow title={title} placement={"right"}>
					<IconButton onClick={showMenu} sx={{ mx: 1.5 }}>
						{icon}
					</IconButton>
				</Tooltip>
			)}

			<Popover id={id} open={open} anchorEl={anchor} anchorOrigin={{ vertical: "center", horizontal: "right" }}>
				<ClickAwayListener onClickAway={closeMenu}>
					<MenuList autoFocusItem>
						{options.map((o) => (
							<div key={(o.value as string) ?? o.text}>
								<MenuItem
									selected={o.value === value}
									onClick={() => didSelectOption(o)}
									sx={{ height: "40px" }}
								>
									{o.icon}
									<span style={{ marginLeft: o.icon ? "8px" : 0 }}>{o.text}</span>
								</MenuItem>
								{o.showDivider && <Divider sx={{ m: "0!important" }} />}
							</div>
						))}
					</MenuList>
				</ClickAwayListener>
			</Popover>
		</ListItem>
	);
};

export default SelectItem;
